import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Phone } from 'lucide-react';
import { Layout } from '../layout/Layout';
import { Breadcrumbs } from './Breadcrumbs';

const PHONE = '(212) 209-0654';

export const TrustHero = ({ eyebrow, title, intro, testId }) => (
  <section className="relative pt-36 pb-20 md:pt-40 md:pb-24 bg-neutral-900 overflow-hidden" data-testid={`${testId}-hero`}>
    <div className="absolute inset-0 opacity-20 bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.05)_25%,rgba(255,255,255,0.05)_50%,transparent_50%,transparent_75%,rgba(255,255,255,0.05)_75%)] bg-[length:60px_60px]" />
    <div className="container-xl relative">
      <Breadcrumbs light className="mb-8" />
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
        <span className="label-overline text-amber-400">{eyebrow}</span>
        <h1 className="text-white mt-4 mb-6" data-testid={`${testId}-title`}>{title}</h1>
        <p className="text-lg md:text-xl text-white/75 leading-relaxed">{intro}</p>
      </motion.div>
    </div>
  </section>
);

export const TrustSection = ({ title, children, muted = false, testId }) => (
  <section className={`py-16 md:py-20 ${muted ? 'bg-neutral-50' : 'bg-white'}`} data-testid={testId}>
    <div className="container-xl grid lg:grid-cols-[280px_1fr] gap-6 lg:gap-16">
      <h2 className="!text-2xl md:!text-3xl font-serif text-neutral-900">{title}</h2>
      <div className="max-w-3xl text-neutral-700 leading-relaxed space-y-4">{children}</div>
    </div>
  </section>
);

export const TrustList = ({ items, testId }) => (
  <ul className="grid sm:grid-cols-2 gap-3" data-testid={testId}>
    {items.map((it) => (
      <li key={it.title} className="border border-neutral-200 bg-white p-5">
        <p className="font-medium text-neutral-900">{it.title}</p>
        {it.text && <p className="text-sm text-neutral-600 mt-1">{it.text}</p>}
      </li>
    ))}
  </ul>
);

export const RelatedLinks = ({ links }) => (
  <div className="flex flex-wrap gap-3 pt-2" data-testid="trust-related-links">
    {links.map((l) => (
      <Link key={l.to} to={l.to} className="inline-flex items-center gap-2 border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-800 hover:border-neutral-900 transition-colors">
        {l.label} <ArrowRight className="w-4 h-4" />
      </Link>
    ))}
  </div>
);

export const TrustCta = ({ title, text }) => (
  <section className="py-20 bg-amber-50" data-testid="trust-cta">
    <div className="container-xl max-w-3xl text-center">
      <h2 className="!text-3xl md:!text-4xl font-serif text-neutral-900 mb-4">{title}</h2>
      <p className="text-lg text-neutral-600 mb-8">{text}</p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Link to="/consultation" className="btn-primary px-8 py-4" data-testid="trust-cta-consultation">Book a Free Consultation</Link>
        <Link to="/quote" className="btn-outline px-8 py-4" data-testid="trust-cta-quote">Request a Quote</Link>
      </div>
      <a href="tel:2122090654" className="mt-6 inline-flex items-center gap-2 text-neutral-700 hover:text-amber-700" data-testid="trust-cta-call">
        <Phone className="w-4 h-4" /> Or call {PHONE}
      </a>
    </div>
  </section>
);

export const TrustLayout = ({ children }) => <Layout><div className="bg-white">{children}</div></Layout>;
